import { FC, ReactNode } from 'react';
import { Box } from 'src/components/ui';

// Magazine page aspect ratio (standard US letter: 8.5" x 11")
const PAGE_ASPECT_RATIO = 8.5 / 11;

// Placement configurations with positions and aspect ratios
// These define where the ad appears and its native aspect ratio
const PLACEMENT_CONFIG: Record<string, {
  top: string;
  left: string;
  width: string;
  height: string;
  // Native aspect ratio of this placement (width/height)
  aspectRatio: number;
}> = {
  magazineFullPage: {
    top: '0%',
    left: '0%',
    width: '100%',
    height: '100%',
    aspectRatio: 2588 / 3338, // ~0.775 (vertical)
  },
  magazineHalfHorizontal: {
    // Bottom half of the page
    top: '50%',
    left: '0%',
    width: '100%',
    height: '50%',
    aspectRatio: 2250 / 1388, // ~1.62 (horizontal)
  },
  magazineHalfVertical: {
    // Right side of the page
    top: '0%',
    left: '50%',
    width: '50%',
    height: '100%',
    aspectRatio: 1463 / 2138, // ~0.68 (vertical)
  },
  magazineThirdSquare: {
    // Bottom-right corner
    top: '58%',
    left: '50%',
    width: '50%',
    height: '42%',
    aspectRatio: 1463 / 1388, // ~1.05 (square-ish)
  },
};

// Component to scale content to fill container
// Uses CSS to force the Creatomate preview to fill its container
const ScaledContent: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    position="absolute"
    top="0"
    left="0"
    right="0"
    bottom="0"
    overflow="hidden"
    sx={{
      // Target the Creatomate preview wrapper
      '& > div': {
        width: '100% !important',
        height: '100% !important',
        position: 'absolute !important',
        top: '0 !important',
        left: '0 !important',
      },
      // Force the inner preview container to fill
      '& > div > div': {
        width: '100% !important',
        height: '100% !important',
        display: 'flex !important',
        alignItems: 'center !important',
        justifyContent: 'center !important',
      },
      // Scale the canvas/iframe to cover the container
      '& canvas, & iframe, & video': {
        width: '100% !important',
        height: '100% !important',
        objectFit: 'fill !important',
      },
      // Target Creatomate's specific structure
      '& [data-creatomate], & .creatomate-preview': {
        width: '100% !important',
        height: '100% !important',
      },
    }}
  >
    {children}
  </Box>
);

interface MagazinePageMockupProps {
  placement: string;
  children: ReactNode;
  height?: { base: string; md: string };
}

const MagazinePageMockup: FC<MagazinePageMockupProps> = ({
  placement,
  children,
  height = { base: '350px', md: '450px' },
}) => {
  const isFullPage = placement === 'magazineFullPage';
  const config = PLACEMENT_CONFIG[placement] || PLACEMENT_CONFIG.magazineHalfHorizontal;

  // For full page, render with proper aspect ratio container
  if (isFullPage) {
    return (
      <Box
        h={height}
        w="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        bg="gray.100"
        p={4}
      >
        <Box
          position="relative"
          h="100%"
          sx={{
            aspectRatio: `${config.aspectRatio}`,
          }}
          bg="white"
          boxShadow="xl"
          borderRadius="sm"
          overflow="hidden"
          border="1px solid"
          borderColor="gray.300"
        >
          <ScaledContent>{children}</ScaledContent>
        </Box>
      </Box>
    );
  }

  // For partial placements, show the magazine page mockup
  return (
    <Box
      h={height}
      w="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      bg="gray.100"
      p={4}
    >
      {/* Magazine Page Container - maintains page aspect ratio */}
      <Box
        position="relative"
        h="100%"
        sx={{
          aspectRatio: `${PAGE_ASPECT_RATIO}`,
        }}
        bg="white"
        boxShadow="xl"
        borderRadius="sm"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.300"
      >
        {/* Diagonal stripe pattern for unavailable areas */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          sx={{
            background: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 8px,
              rgba(239, 68, 68, 0.15) 8px,
              rgba(239, 68, 68, 0.15) 16px
            )`,
          }}
          zIndex={1}
          pointerEvents="none"
        />

        {/* "Not Available" watermark for unavailable area */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={2}
          pointerEvents="none"
        >
          <Box
            color="red.300"
            fontSize={{ base: 'xs', md: 'sm' }}
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wider"
            opacity={0.6}
            transform="rotate(-45deg)"
            whiteSpace="nowrap"
          >
            Other Content
          </Box>
        </Box>

        {/* The actual ad placement area */}
        <Box
          position="absolute"
          top={config.top}
          left={config.left}
          width={config.width}
          height={config.height}
          zIndex={3}
          bg="white"
          boxShadow="md"
          overflow="hidden"
        >
          {/* Red border to highlight the ad area */}
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            border="2px solid"
            borderColor="red.400"
            zIndex={4}
            pointerEvents="none"
            borderRadius="sm"
          />

          {/* Ad content - scaled to fill */}
          <ScaledContent>{children}</ScaledContent>
        </Box>

        {/* Page fold effect (subtle) */}
        <Box
          position="absolute"
          top="0"
          left="50%"
          width="1px"
          height="100%"
          bg="gray.200"
          zIndex={5}
          pointerEvents="none"
          opacity={0.5}
        />
      </Box>
    </Box>
  );
};

export default MagazinePageMockup;
